import React, { useState } from 'react';
import { saveOnboarding, pushRemoteOnboarding } from '../services/onboardingStore';

// ─── Config ───────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || 'https://api.nowatt.app';

const SUPPLIERS = [
  { name: 'EDF',           domain: 'particuliers.edf.fr'  },
  { name: 'Engie',         domain: 'engie.fr'              },
  { name: 'TotalEnergies', domain: 'totalenergies.fr'      },
  { name: 'Octopus',       domain: 'octopusenergy.fr'      },
  { name: 'Primeo',        domain: 'primeo-energie.fr'     },
  { name: 'OHM Energie',   domain: 'ohm-energie.com'       },
  { name: 'Vattenfall',    domain: 'vattenfall.fr'         },
  { name: 'Ilek',          domain: 'ilek.fr'               },
  { name: 'Ekwateur',      domain: 'ekwateur.fr'           },
  { name: 'Alpiq',         domain: 'alpiq.com'             },
  { name: 'Mint Energie',  domain: 'mint-energie.com'      },
  { name: 'Alterna',       domain: 'alterna-energie.fr'    },
];

const PRESETS = [
  { label: 'Studio',        kwh: 2000,  icon: '🏠', hint: '~2 000 kWh' },
  { label: 'Appartement',   kwh: 4500,  icon: '🏢', hint: '~4 500 kWh' },
  { label: 'Maison',        kwh: 8000,  icon: '🏡', hint: '~8 000 kWh' },
  { label: 'Grande maison', kwh: 12000, icon: '🏘', hint: '~12 000 kWh' },
];

const getLogo = (domain) =>
  `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

// ─── Styles partagés ──────────────────────────────────────────────────────────
const S = {
  base:    { fontFamily: "'Plus Jakarta Sans', sans-serif" },
  outfit:  { fontFamily: "'Lexend', sans-serif"  },
  lime:    '#C8F135',
  dark:    '#06080C',
  card:    '#111620',
  text:    '#EEF2FF',
  muted:   'rgba(238,242,255,0.4)',
  border:  'rgba(238,242,255,0.08)',
};

// ─── Composant principal ──────────────────────────────────────────────────────
export default function Onboarding({ user, onDone }) {
  const [step,             setStep]             = useState(1);
  const [supplier,         setSupplier]         = useState(null);
  const [kwh,              setKwh]              = useState(4500);
  const [analysing,        setAnalysing]        = useState(false);
  const [result,           setResult]           = useState(null);

  // ── Calculs ─────────────────────────────────────────────────────────────────
  // Coût actuel : celui calculé par le serveur ; à défaut, tarif réglementé Base 9 kVA (août 2026)
  const currentCost = parseFloat((result?.current_annual_cost ?? (kwh * 0.1985 + 238.60)).toFixed(2));
  // Économie sur la facture, calculée par le serveur
  const savings     = result
    ? Math.round(result.annual_savings ?? Math.max(0, currentCost - result.best_annual_cost))
    : 0;

  // ── Actions ─────────────────────────────────────────────────────────────────
  const saveLocal = () => {
    const answers = { supplier: supplier?.name || null, kwh };
    saveOnboarding(user?.user_id, answers);
    pushRemoteOnboarding(user?.access_token, answers); // suit l'utilisateur sur ses autres appareils
  };

  const handleAnalyse = async () => {
    setStep(3);
    setAnalysing(true);
    try {
      const res  = await fetch(`${API}/recommendation/best-offer`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ annual_kwh: kwh }),
      });
      const data = await res.json();
      setTimeout(() => { setResult(data); setAnalysing(false); }, 2200);
    } catch {
      setTimeout(() => setAnalysing(false), 2200);
    }
  };

  const handleSubscribe = async () => {
    saveLocal();
    try {
      const res  = await fetch(`${API}/stripe/create-checkout-session`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${user?.access_token}`,
        },
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; return; }
    } catch { /* ignore */ }
    onDone();
  };

  // « Passer » : on n'enregistre aucune consommation que l'utilisateur n'a pas saisie lui-même
  const handleSkip = () => {
    const answers = { supplier: supplier?.name || null, kwh: step >= 3 ? kwh : null };
    saveOnboarding(user?.user_id, answers);
    pushRemoteOnboarding(user?.access_token, answers);
    onDone();
  };

  // ── Layout racine ────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight:       '100vh',
      background:      S.dark,
      color:           S.text,
      display:         'flex',
      flexDirection:   'column',
      alignItems:      'center',
      paddingBottom:   48,
      ...S.base,
    }}>
      {/* ── Topbar ── */}
      <div style={{
        width: '100%', maxWidth: 520, padding: '20px 24px 0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxSizing: 'border-box',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src="/favicon_nowatt.png"
            alt="Nowatt"
            style={{ width: 40, height: 40, objectFit: 'contain', mixBlendMode: 'screen' }}
          />
          <span style={{ ...S.outfit, fontWeight: 700, color: '#EEF2FF', fontSize: '1rem' }}>
            <span style={{ color: S.lime }}>No</span>watt
          </span>
        </div>
        {step < 3 && (
          <button onClick={handleSkip} style={{
            background: 'none', border: 'none', color: S.muted,
            cursor: 'pointer', fontSize: '0.8rem', ...S.base,
          }}>
            Passer →
          </button>
        )}
      </div>

      {/* ── Barre de progression ── */}
      <div style={{
        width: '100%', maxWidth: 520, padding: '16px 24px 0',
        boxSizing: 'border-box',
      }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[1, 2, 3].map(s => (
            <div key={s} style={{
              flex: 1, height: 3, borderRadius: 10,
              background: s <= step ? S.lime : 'rgba(238,242,255,0.1)',
              transition: 'background 0.4s ease',
            }} />
          ))}
        </div>
        <p style={{
          margin: '6px 0 0', fontSize: '0.7rem', color: S.muted,
          textAlign: 'right',
        }}>
          Étape {step} / 3
        </p>
      </div>

      {/* ── Zone de contenu ── */}
      <div style={{
        width: '100%', maxWidth: 520, padding: '24px 24px 0',
        boxSizing: 'border-box', flex: 1,
      }}>

        {/* ════════════ ÉTAPE 1 — Fournisseur ════════════ */}
        {step === 1 && (
          <Step1
            supplier={supplier}
            onSelect={setSupplier}
            onNext={() => setStep(2)}
          />
        )}

        {/* ════════════ ÉTAPE 2 — Consommation ════════════ */}
        {step === 2 && (
          <Step2
            kwh={kwh}
            onKwh={setKwh}
            onBack={() => setStep(1)}
            onNext={handleAnalyse}
          />
        )}

        {/* ════════════ ÉTAPE 3 — Résultat ════════════ */}
        {step === 3 && (
          <Step3
            analysing={analysing}
            result={result}
            savings={savings}
            currentCost={currentCost}
            onSubscribe={handleSubscribe}
            onSkip={handleSkip}
          />
        )}

      </div>
    </div>
  );
}

// ─── ÉTAPE 1 ──────────────────────────────────────────────────────────────────
function Step1({ supplier, onSelect, onNext }) {
  return (
    <div>
      <h1 style={{ margin: '0 0 6px', fontSize: '1.55rem', fontWeight: 700, lineHeight: 1.2 }}>
        Quel est votre fournisseur ?
      </h1>
      <p style={{ margin: '0 0 24px', color: S.muted, fontSize: '0.87rem' }}>
        Sélectionnez votre fournisseur d'électricité actuel
      </p>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 10, marginBottom: 28,
      }}>
        {SUPPLIERS.map(s => {
          const active = supplier?.name === s.name;
          return (
            <button
              key={s.name}
              onClick={() => onSelect(s)}
              style={{
                background:  active ? 'rgba(200,241,53,0.13)' : S.card,
                border:      `1.5px solid ${active ? S.lime : S.border}`,
                borderRadius: 12, padding: '14px 8px',
                cursor:       'pointer',
                display:      'flex', flexDirection: 'column',
                alignItems:   'center', gap: 8,
                transition:   'all 0.18s ease',
                ...S.base,
              }}
            >
              <img
                src={getLogo(s.domain)}
                alt={s.name}
                onError={e => { e.target.style.display = 'none'; }}
                style={{
                  width: 28, height: 28, borderRadius: 6,
                  background: 'white', padding: 2, objectFit: 'contain',
                }}
              />
              <span style={{
                fontSize: '0.68rem', fontWeight: 600, textAlign: 'center', lineHeight: 1.2,
                color: active ? S.lime : 'rgba(238,242,255,0.7)',
              }}>
                {s.name}
              </span>
            </button>
          );
        })}
      </div>

      <PrimaryBtn disabled={!supplier} onClick={onNext}>
        Continuer →
      </PrimaryBtn>
    </div>
  );
}

// ─── ÉTAPE 2 ──────────────────────────────────────────────────────────────────
function Step2({ kwh, onKwh, onBack, onNext }) {
  return (
    <div>
      <button onClick={onBack} style={{
        background: 'none', border: 'none', color: S.muted,
        cursor: 'pointer', marginBottom: 16, padding: 0,
        fontSize: '0.85rem', ...S.base,
      }}>
        ← Retour
      </button>

      <h1 style={{ margin: '0 0 6px', fontSize: '1.55rem', fontWeight: 700, lineHeight: 1.2 }}>
        Votre consommation annuelle
      </h1>
      <p style={{ margin: '0 0 24px', color: S.muted, fontSize: '0.87rem' }}>
        Ajustez selon votre logement — modifiable ensuite
      </p>

      {/* Affichage du kWh */}
      <div style={{
        background:  S.card, borderRadius: 16, padding: '24px',
        textAlign:   'center', marginBottom: 20,
        border:      '1px solid rgba(200,241,53,0.15)',
      }}>
        <p style={{ margin: '0 0 4px', color: S.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 1 }}>
          Consommation estimée
        </p>
        <p style={{ margin: '0 0 2px', color: S.lime, fontWeight: 700, fontSize: '3.2rem', lineHeight: 1, ...S.outfit }}>
          {kwh.toLocaleString()}
        </p>
        <p style={{ margin: 0, color: 'rgba(238,242,255,0.5)', fontSize: '0.9rem' }}>kWh / an</p>
      </div>

      {/* Slider */}
      <input
        type="range" min={1000} max={15000} step={100} value={kwh}
        onChange={e => onKwh(Number(e.target.value))}
        style={{ width: '100%', accentColor: S.lime, marginBottom: 6 }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: S.muted, marginBottom: 26 }}>
        <span>1 000</span><span>15 000 kWh</span>
      </div>

      {/* Presets */}
      <p style={{ margin: '0 0 10px', fontSize: '0.72rem', color: S.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
        Aide au choix
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 28 }}>
        {PRESETS.map(p => {
          const active = kwh === p.kwh;
          return (
            <button
              key={p.label}
              onClick={() => onKwh(p.kwh)}
              style={{
                background:   active ? 'rgba(200,241,53,0.12)' : S.card,
                border:       `1.5px solid ${active ? S.lime : S.border}`,
                borderRadius: 10, padding: '12px',
                cursor:       'pointer', textAlign: 'left',
                display:      'flex', flexDirection: 'column', gap: 4,
                ...S.base,
              }}
            >
              <span style={{ fontSize: '1.1rem' }}>{p.icon}</span>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: active ? S.lime : S.text }}>
                {p.label}
              </span>
              <span style={{ fontSize: '0.68rem', color: S.muted }}>{p.hint}</span>
            </button>
          );
        })}
      </div>

      <PrimaryBtn onClick={onNext}>
        Analyser mon contrat ⚡
      </PrimaryBtn>
    </div>
  );
}

// ─── ÉTAPE 3 ──────────────────────────────────────────────────────────────────
function Step3({ analysing, result, savings, currentCost, onSubscribe, onSkip }) {
  if (analysing) return <AnalysingLoader />;

  return (
    <div style={{ animation: 'fadeUp 0.45s ease', textAlign: 'center' }}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
      `}</style>

      {/* ── Carte économies ── */}
      <div style={{
        background:   'linear-gradient(135deg, #0d1a00, #081000)',
        borderRadius: 20, padding: '30px 24px',
        border:       '1px solid rgba(200,241,53,0.25)',
        marginBottom: 14, textAlign: 'center',
      }}>
        <p style={{ margin: '0 0 6px', color: 'rgba(200,241,53,0.6)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 1 }}>
          Vous pourriez économiser
        </p>
        <p style={{ margin: '0 0 4px', color: S.lime, fontWeight: 700, fontSize: '3.6rem', lineHeight: 1, ...S.outfit }}>
          {savings}€
        </p>
        <p style={{ margin: '0 0 8px', color: 'rgba(200,241,53,0.5)', fontSize: '0.83rem' }}>
          par an sur votre facture, en changeant de fournisseur
        </p>
        <p style={{ margin: '0 0 22px', color: 'rgba(238,242,255,0.75)', fontSize: '0.78rem', lineHeight: 1.5 }}>
          <span style={{ color: 'rgba(238,242,255,0.4)', fontSize: '0.7rem' }}>
            Estimation pour un compteur 9 kVA en option Base, comparée au tarif réglementé.
          </span>
        </p>

        {/* Ligne contrat actuel */}
        <div style={{
          background: 'rgba(238,242,255,0.04)', borderRadius: 10, padding: '11px 16px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8,
        }}>
          <span style={{ fontSize: '0.8rem', color: S.muted }}>Votre contrat actuel</span>
          <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{currentCost}€/an</span>
        </div>

        {/* Ligne meilleure offre — prix visible, fournisseur masqué */}
        <div style={{
          background: 'rgba(200,241,53,0.06)', borderRadius: 10, padding: '11px 16px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: '0.8rem', color: S.muted }}>Meilleure offre 🔒</span>
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: S.lime }}>
            {result?.best_annual_cost}€/an
          </span>
        </div>
      </div>

      {/* ── Fournisseur masqué ── */}
      <div style={{
        background: S.card, borderRadius: 14, padding: '16px',
        border: `1px solid ${S.border}`, marginBottom: 20, textAlign: 'left',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: '1rem' }}>🔒</span>
          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.88rem' }}>Offre recommandée par Nowatt</p>
          <span style={{
            marginLeft: 'auto', background: 'rgba(200,241,53,0.12)',
            color: S.lime, fontSize: '0.65rem', padding: '2px 8px', borderRadius: 20,
          }}>Moins chère</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'rgba(238,242,255,0.08)',
            filter: 'blur(4px)', flexShrink: 0,
          }} />
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem', filter: 'blur(5px)', userSelect: 'none' }}>
              ██████████
            </p>
            <p style={{ margin: '3px 0 0', fontSize: '0.73rem', color: S.muted, filter: 'blur(4px)', userSelect: 'none' }}>
              ██████ ████████
            </p>
          </div>
          <span style={{ color: S.lime, fontWeight: 700, filter: 'blur(5px)', userSelect: 'none', flexShrink: 0 }}>
            ██€/kWh
          </span>
        </div>
      </div>

      {/* ── Argument unique Nowatt ── */}
      <div style={{
        background: S.card, borderRadius: 14, padding: '14px 16px',
        border:     `1px solid ${S.border}`, marginBottom: 22, textAlign: 'left',
        display:    'flex', alignItems: 'flex-start', gap: 12,
      }}>
        <span style={{ fontSize: '1.3rem', flexShrink: 0, marginTop: 2 }}>📈</span>
        <div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem' }}>
            Si vous ne changez pas cette année
          </p>
          <p style={{ margin: '4px 0 0', color: S.muted, fontSize: '0.78rem' }}>
            Vous aurez perdu <strong style={{ color: S.lime }}>{savings}€</strong> supplémentaires
            dans 12 mois. Débloquez l'offre pour savoir laquelle choisir.
          </p>
        </div>
      </div>

      {/* ── CTA principal ── */}
      <button
        onClick={onSubscribe}
        style={{
          width:        '100%', padding: '16px', border: 'none', borderRadius: 12,
          background:   S.lime, color: S.dark,
          fontWeight:   700, fontSize: '1rem', cursor: 'pointer',
          marginBottom: 10, ...S.outfit,
        }}
      >
        Débloquer pour 5,99€/mois →
      </button>
      <p style={{ margin: '0 0 18px', color: 'rgba(238,242,255,0.22)', fontSize: '0.7rem' }}>
        Sans engagement — Résiliable à tout moment
      </p>

      {/* ── Lien passer ── */}
      <button
        onClick={onSkip}
        style={{
          background: 'none', border: 'none', color: 'rgba(238,242,255,0.28)',
          cursor: 'pointer', fontSize: '0.78rem', textDecoration: 'underline',
          ...S.base,
        }}
      >
        Continuer sans m'abonner
      </button>
    </div>
  );
}

// ─── Loader analyse ───────────────────────────────────────────────────────────
function AnalysingLoader() {
  return (
    <div style={{ paddingTop: 70, textAlign: 'center' }}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1;   }
          50%       { opacity: 0.4; }
        }
      `}</style>
      <div style={{
        width:        72, height: 72, borderRadius: '50%',
        border:       '3px solid rgba(200,241,53,0.15)',
        borderTop:    `3px solid ${S.lime}`,
        margin:       '0 auto 28px',
        animation:    'spin 1s linear infinite',
      }} />
      <h2 style={{ margin: '0 0 10px', fontSize: '1.25rem', fontWeight: 700 }}>
        Nowatt analyse le marché...
      </h2>
      <p style={{ margin: '0 0 28px', color: S.muted, fontSize: '0.83rem' }}>
        Comparaison des offres en cours
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 260, margin: '0 auto' }}>
        {['Lecture de votre profil', 'Comparaison des offres', 'Calcul des économies'].map((t, i) => (
          <div key={t} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            animation: `pulse ${1 + i * 0.3}s ease-in-out infinite`,
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%', background: S.lime,
            }} />
            <span style={{ fontSize: '0.8rem', color: S.muted }}>{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Bouton primaire réutilisable ─────────────────────────────────────────────
function PrimaryBtn({ children, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width:        '100%', padding: '15px', border: 'none', borderRadius: 12,
        background:   disabled ? 'rgba(238,242,255,0.08)' : S.lime,
        color:        disabled ? 'rgba(238,242,255,0.25)' : S.dark,
        fontWeight:   700, fontSize: '1rem',
        cursor:       disabled ? 'not-allowed' : 'pointer',
        transition:   'all 0.2s ease',
        ...S.outfit,
      }}
    >
      {children}
    </button>
  );
}
